import { isProductionApi } from '@/util/helpers'
import heroImage from '@/assets/landing/brazil-elections-2026-urna.jpg'
import { pathnameWithoutLocalePrefix } from '@/util/localePath'

/** Landing route. Evergreen slug with the cycle year; the next cycle gets its own path. */
export const BRAZIL_ELECTIONS_PATH = '/elections/brazil-2026'

/** Polls open at 08:00 BRT (UTC-3) on the first-round Sunday. */
export const BRAZIL_ELECTIONS_FIRST_ROUND_DATE = '2026-10-04T11:00:00Z'
export const BRAZIL_ELECTIONS_SECOND_ROUND_DATE = '2026-10-25T11:00:00Z'

/**
 * Resolved markets that closed before this date are campaign side questions
 * (pre-candidacies, party switches) and stay off the landing; the races closed
 * on election week or later show their result.
 */
export const BRAZIL_ELECTIONS_RESULTS_SINCE = '2026-10-01T00:00:00Z'

/**
 * Full-bleed photo behind the opening screen, shipped already in black and
 * white at 2000px. Set to '' to fall back to the two front-runners' portraits.
 *
 * "TSE apresenta urna eletrônica durante feira de ciência em escola de Santa
 * Maria" — Tribunal Superior Eleitoral, public domain, via Wikimedia Commons:
 * https://commons.wikimedia.org/wiki/File:TSE_apresenta_urna_eletr%C3%B4nica_durante_feira_de_ci%C3%AAncia_em_escola_de_Santa_Maria.jpg
 */
export const BRAZIL_ELECTIONS_HERO_IMAGE = heroImage

export const BRAZIL_ELECTIONS_RACE = {
  GOVERNOR: 'governor',
  SENATE: 'senate',
}

/** Sub-categories of the election category on the exchange, used to label races. */
export const BRAZIL_ELECTIONS_SUBCATEGORY = {
  GOVERNORS: 3713,
  SENATE: 3712,
}

/**
 * The election markets only exist on the production database; the same ids
 * point at unrelated markets on staging. Every id below is a production id.
 */
export const BRAZIL_ELECTIONS = {
  /** Politics > Brazil > 2026 Election */
  categoryId: 3508,
  categoryPath: '/markets/politics/brazil/2026-election/3508',
  /** "Brazil's Next President Elect" */
  presidentialMarketId: 233774,
  /** The two outcomes the duel always shows: Lula, Flávio Bolsonaro. */
  presidentialOutcomeIds: [574938, 574939],
  /** "Will Brazil's presidential election be decided in the first round?" */
  firstRoundMarketId: 235763,
  /** Governor races in display order: MG, AL, SP, RS, PR, RJ. */
  trendingMarketIds: [236763, 236764, 236761, 236766, 243303, 236762],
}

/**
 * Only a build pointed at the production exchange renders live markets; local,
 * staging and PR previews serve the snapshot in `mockMarkets.js` and are
 * marked `noindex`, so placeholder probabilities never reach search results.
 */
export const BRAZIL_ELECTIONS_USE_MOCK = !isProductionApi()

/** Markets with a dedicated section; everything else in the category lands in the ledger. */
export const BRAZIL_ELECTIONS_FEATURED_IDS = [
  BRAZIL_ELECTIONS.presidentialMarketId,
  BRAZIL_ELECTIONS.firstRoundMarketId,
  ...BRAZIL_ELECTIONS.trendingMarketIds,
]

export function isBrazilElectionsPath(pathname) {
  const clean = pathnameWithoutLocalePrefix(String(pathname || '')).replace(/\/$/, '')
  return clean === BRAZIL_ELECTIONS_PATH
}

/** Brazilian state names as they appear in market titles, mapped to the UF code. */
export const BRAZIL_STATES = {
  Acre: 'AC',
  Alagoas: 'AL',
  Amapá: 'AP',
  Amazonas: 'AM',
  Bahia: 'BA',
  Ceará: 'CE',
  'Distrito Federal': 'DF',
  'Espírito Santo': 'ES',
  Goiás: 'GO',
  Maranhão: 'MA',
  'Mato Grosso': 'MT',
  'Mato Grosso do Sul': 'MS',
  'Minas Gerais': 'MG',
  Pará: 'PA',
  Paraíba: 'PB',
  Paraná: 'PR',
  Pernambuco: 'PE',
  Piauí: 'PI',
  'Rio de Janeiro': 'RJ',
  'Rio Grande do Norte': 'RN',
  'Rio Grande do Sul': 'RS',
  Rondônia: 'RO',
  Roraima: 'RR',
  'Santa Catarina': 'SC',
  'São Paulo': 'SP',
  Sergipe: 'SE',
  Tocantins: 'TO',
}
